import { useTar, apis } from '@/modules'
import { useAuth } from '@/hooks/useAuth'
import { useAnalytics } from '@/hooks/useAnalytics'
import { useTheme } from '@/contexts/ThemeContext'
import { UserAvatar } from '../UserAvatar'
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    Switch,
    SinglePick,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@duck/ui'
import { Settings, Moon, Sun, Sparkles, Globe } from 'lucide-react'
import { useUserSettings } from '@/hooks/useUserSettings'
import { useAtomValue, useSetAtom } from 'jotai'
import { userAtom, setUserAtom } from '@/store/userAtoms'
import { UserService } from '@duck/api-definitions'
import { allCountries } from '@/modules/countries'
import { useMemo } from 'react'
import { SettingRow } from '@/components/settings'

const RecruitmentInfoDialog = () => {
    const { t } = useTar()
    return (
        <Dialog>
            <DialogTrigger asChild>
                <button className="text-sm text-primary hover:underline">
                    {t('userSettings.howThisWorks')}
                </button>
            </DialogTrigger>
            <DialogContent width="xs" noMinHeight>
                <DialogHeader>
                    <DialogTitle>{t('userSettings.recruitmentInfo.title')}</DialogTitle>
                </DialogHeader>
                <ul className="space-y-3 text-sm text-foreground">
                    <li className="flex gap-2">
                        <span className="text-primary">•</span>
                        <span>{t('userSettings.recruitmentInfo.step1')}</span>
                    </li>
                    <li className="flex gap-2">
                        <span className="text-primary">•</span>
                        <span>{t('userSettings.recruitmentInfo.step2')}</span>
                    </li>
                    <li className="flex gap-2">
                        <span className="text-primary">•</span>
                        <span>{t('userSettings.recruitmentInfo.step3')}</span>
                    </li>
                </ul>
                <div className="mt-4 pt-4 border-t border-border space-y-1 text-sm">
                    <p>{t('userSettings.recruitmentInfo.anonymousNote')}</p>
                    <p>{t('userSettings.recruitmentInfo.optOutNote')}</p>
                </div>
            </DialogContent>
        </Dialog>
    )
}

export const UserAreaPage = () => {
    const { t } = useTar()
    const { user, displayName, isAuthenticated, openLoginDialog } = useAuth()
    const { setTheme, isDark } = useTheme()
    const { getSetting, setSetting } = useUserSettings()
    const currentUser = useAtomValue(userAtom)
    const setUser = useSetAtom(setUserAtom)
    const { capture, getFlag } = useAnalytics()
    const exposureABVariant = useMemo(() => getFlag('recruitment-exposure-setting', 'control'), [getFlag])

    const handleCountryChange = async (country?: string) => {
        if (!country) return

        const updateRequest: UserService.UpdatePersonalInfoRequest = {
            country,
        }

        const { data: updatedUser } = await apis.userService.updatePersonalInfoPut(updateRequest)
        setUser(updatedUser)
    }

    if (!isAuthenticated || !user) {
        return (
            <div className="flex flex-1 w-full justify-center">
                <div className="flex-1 max-w-2xl space-y-8 mt-16 mb-16 px-4">
                    <div className="text-center space-y-6">
                        <div className="w-20 h-20 mx-auto bg-muted rounded-full flex items-center justify-center">
                            <Settings className="w-10 h-10 text-muted-foreground" />
                        </div>
                        <h1 className="text-3xl font-bold text-foreground">
                            {t('common.userArea')}
                        </h1>
                        <p className="text-lg text-muted-foreground">
                            {t('errors.unauthorizedDescription')}
                        </p>
                        <Button onClick={openLoginDialog} size="lg">
                            {t('common.login')}
                        </Button>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="flex flex-1 w-full justify-center">
            <div className="flex-1 max-w-4xl space-y-8 mt-8 mb-16 px-4">
                {/* Profile Header */}
                <Card className="border-border">
                    <CardContent className="pt-6">
                        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
                            <UserAvatar size="lg" className="!w-20 !h-20 !text-2xl" />
                            <div className="flex-1 text-center sm:text-left space-y-2">
                                <h1 className="text-2xl md:text-3xl font-bold text-foreground">
                                    {displayName}
                                </h1>
                                <p className="text-muted-foreground">{user.email}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>


                {/* Settings Section */}
                <Card className="border-border">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-4">
                            {t('common.settings')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* Allow Recruitment Exposure */}
                        <SettingRow
                            Icon={Sparkles}
                            title={exposureABVariant === 'control' ? t('userSettings.recruitmentExposureTitleA') : t('userSettings.recruitmentExposureTitleB')}
                            description={exposureABVariant === 'control' ? t('userSettings.recruitmentExposureDescriptionA') : t('userSettings.recruitmentExposureDescriptionB')}
                            titleExtraElement={<RecruitmentInfoDialog />}
                        >
                            <Switch
                                id="allowsAnonymousExposure"
                                checked={getSetting('allowsAnonymousExposure') ?? false}
                                onCheckedChange={(checked) => {
                                    setSetting('allowsAnonymousExposure', checked)
                                    capture(checked ? 'recruitment_exposure_toggled_on' : 'recruitment_exposure_toggled_off')
                                }}
                            />
                        </SettingRow>

                        <div className="border-b border-border" />
                        {/* Country */}
                        <SettingRow
                            Icon={Globe}
                            title={t('common.country')}
                            description={t('personalInfo.countryDescription')}
                        >
                            <SinglePick
                                options={allCountries}
                                selected={currentUser?.personalInfo?.country}
                                onChange={handleCountryChange}
                                placeholder={t('common.selectCountry')}
                                className="w-[160px]"
                            />
                        </SettingRow>

                        <div className="border-b border-border"></div>
                        <SettingRow
                            Icon={isDark ? Moon : Sun}
                            title={t('common.theme')}
                            description={isDark ? t('themes.darkMode') : t('themes.lightMode')}
                        >
                            <div className="flex items-center p-1 gap-0.5">
                                <button
                                    onClick={() => setTheme('light')}
                                    className={`p-2 rounded-full transition-colors ${!isDark
                                        ? 'bg-background text-foreground shadow-sm'
                                        : 'text-muted-foreground hover:text-foreground'
                                        }`}
                                    aria-label="Light mode"
                                >
                                    <Sun className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => setTheme('dark')}
                                    className={`p-2 rounded-full transition-colors ${isDark
                                        ? 'bg-background text-foreground shadow-sm'
                                        : 'text-muted-foreground hover:text-foreground'
                                        }`}
                                    aria-label="Dark mode"
                                >
                                    <Moon className="w-4 h-4" />
                                </button>
                            </div>
                        </SettingRow>
                    </CardContent>
                </Card>
            </div>
        </div>
    )
}
